import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import {
  engineeringService,
  type FlowStage,
  type RunStageOut,
} from '@/services/engineering';
import { useAuth } from '@/store/useAuth';

import styles from './Engineering.module.css';

export function Engineering() {
  const role = useAuth((s) => s.role);
  const qc = useQueryClient();

  const [flowId, setFlowId] = useState<string | null>(null);
  const [tema, setTema] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [outputs, setOutputs] = useState<Record<string, RunStageOut>>({});

  const flowsQuery = useQuery({
    queryKey: ['eng-flows'],
    queryFn: engineeringService.listFlows,
    enabled: role === 'admin',
  });

  // seleciona o primeiro fluxo assim que o rundown carrega
  useEffect(() => {
    if (!flowId && flowsQuery.data && flowsQuery.data.length > 0) {
      setFlowId(flowsQuery.data[0].id);
    }
  }, [flowsQuery.data, flowId]);

  const flowQuery = useQuery({
    queryKey: ['eng-flow', flowId],
    queryFn: () => engineeringService.getFlow(flowId as string),
    enabled: Boolean(flowId),
  });

  // troca de fluxo = estado limpo (edições + saídas + entrada do run)
  useEffect(() => {
    setEdits({});
    setOutputs({});
    setTema('');
  }, [flowId]);

  // semeia o texto editável de cada estágio sem clobber de edição em curso
  // (preserva edições e saídas entre refetches; só semeia o que ainda não existe)
  useEffect(() => {
    if (!flowQuery.data) return;
    setEdits((prev) => {
      const next = { ...prev };
      for (const s of flowQuery.data.stages) {
        if (next[s.id] === undefined) next[s.id] = s.role_system;
      }
      return next;
    });
  }, [flowQuery.data]);

  const save = useMutation({
    mutationFn: (stage: FlowStage) =>
      engineeringService.savePrompt(flowId as string, stage.id, edits[stage.id] ?? ''),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eng-flow', flowId] }),
  });

  // Restaurar padrão = grava vazio (backend remove o override → volta ao prompt
  // de fábrica), e repõe a textarea com o default localmente.
  const restore = useMutation({
    mutationFn: (stage: FlowStage) =>
      engineeringService.savePrompt(flowId as string, stage.id, ''),
    onSuccess: (_d, stage) => {
      setEdits((p) => ({ ...p, [stage.id]: stage.default_system }));
      qc.invalidateQueries({ queryKey: ['eng-flow', flowId] });
    },
  });

  const run = useMutation({
    mutationFn: (upto?: string) =>
      engineeringService.run(flowId as string, tema, upto),
    onSuccess: (r) => {
      setOutputs((prev) => {
        const next = { ...prev };
        for (const s of r.stages) next[s.id] = s;
        return next;
      });
    },
  });

  if (role !== 'admin') {
    return (
      <section className={styles.root}>
        <div className={styles.restricted}>
          <h1 className={styles.title}>Engenharia de Prompt</h1>
          <p className={styles.muted}>
            Área restrita ao <b>engenheiro de prompt</b> (papel admin). Peça acesso a um
            administrador.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Engenharia de Prompt</h1>
          <p className={styles.subtitle}>
            Ajuste o prompt (Role) de cada estágio dos fluxos. As edições valem por
            tenant e passam a valer na produção. Rode uma etapa ou o fluxo todo num
            tema real para ver a saída.
          </p>
        </div>
      </header>

      <div className={styles.layout}>
        {/* RUNDOWN — fluxos disponíveis */}
        <aside className={styles.rundown}>
          <span className={styles.rundownTitle}>Fluxos</span>
          {flowsQuery.isLoading && <p className={styles.muted}>A carregar…</p>}
          {(flowsQuery.data ?? []).map((f) => (
            <button
              key={f.id}
              type="button"
              className={`${styles.flowRow} ${f.id === flowId ? styles.flowRowActive : ''}`}
              onClick={() => setFlowId(f.id)}
            >
              <span className={styles.flowName}>{f.nome}</span>
              <span className={styles.flowMeta}>{f.estagios} estágios</span>
              <span className={styles.flowDesc}>{f.descricao}</span>
            </button>
          ))}
        </aside>

        {/* CANVAS — estágios do fluxo selecionado */}
        <div className={styles.canvas}>
          {flowQuery.isLoading && <p className={styles.muted}>A carregar fluxo…</p>}
          {flowQuery.data && (
            <>
              <div className={styles.runbar}>
                {(flowQuery.data.run_input?.kind ?? 'text') === 'select' ? (
                  <select
                    className={styles.temaInput}
                    value={tema}
                    onChange={(e) => setTema(e.target.value)}
                    aria-label={flowQuery.data.run_input?.label}
                  >
                    <option value="">— {flowQuery.data.run_input?.label ?? 'Opção'} —</option>
                    {(flowQuery.data.run_input?.options ?? []).map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className={styles.temaInput}
                    value={tema}
                    onChange={(e) => setTema(e.target.value)}
                    placeholder={
                      flowQuery.data.run_input?.placeholder ?? 'Entrada para testar'
                    }
                  />
                )}
                <Button
                  variant="primary"
                  onClick={() => run.mutate(undefined)}
                  disabled={!tema.trim() || run.isPending}
                >
                  {run.isPending && run.variables === undefined
                    ? 'A rodar…'
                    : 'Rodar fluxo todo'}
                </Button>
              </div>

              {run.isError && (
                <p className={styles.err}>
                  Falha ao rodar: {(run.error as Error)?.message || String(run.error)}
                </p>
              )}
              {run.data?.summary && <p className={styles.meta}>{run.data.summary}</p>}

              {flowQuery.data.stages.map((stage) => {
                const value = edits[stage.id] ?? stage.role_system;
                const dirty = value !== stage.role_system;
                const out = outputs[stage.id];
                return (
                  <div key={stage.id} className={styles.stage}>
                    <div className={styles.stageHead}>
                      <span className={styles.stageName}>{stage.nome}</span>
                      <Badge tone="neutral" size="xs">{stage.model}</Badge>
                      {stage.is_overridden && (
                        <Badge tone="warn" size="xs">editado</Badge>
                      )}
                      <span className={styles.spacer} />
                      <Button
                        variant="secondary"
                        onClick={() => run.mutate(stage.id)}
                        disabled={!tema.trim() || run.isPending}
                        title="Roda do começo até esta etapa"
                      >
                        {run.isPending && run.variables === stage.id
                          ? 'A rodar…'
                          : 'Rodar até aqui'}
                      </Button>
                    </div>

                    <div className={styles.cols}>
                      {/* COLUNA BRANCA — Role (editável) + User (referência) */}
                      <div className={styles.promptCol}>
                        <div className={styles.colLabel}>
                          <span className={styles.roleTag}>Role (system)</span>
                          <span className={styles.spacer} />
                          {dirty && (
                            <button
                              type="button"
                              className={styles.linkBtn}
                              onClick={() =>
                                setEdits((p) => ({ ...p, [stage.id]: stage.role_system }))
                              }
                              title="Descarta as alterações não salvas (volta ao Role atual)"
                            >
                              desfazer edição
                            </button>
                          )}
                        </div>
                        <textarea
                          className={styles.promptArea}
                          value={value}
                          onChange={(e) =>
                            setEdits((p) => ({ ...p, [stage.id]: e.target.value }))
                          }
                          spellCheck={false}
                        />
                        <div className={styles.userBlock}>
                          <span className={styles.userTag}>User (montado em runtime)</span>
                          <pre className={styles.userTpl}>{stage.user_template}</pre>
                          {stage.nota && <p className={styles.nota}>ⓘ {stage.nota}</p>}
                        </div>
                        <div className={styles.promptActions}>
                          <Button
                            variant="primary"
                            onClick={() => save.mutate(stage)}
                            disabled={!dirty || save.isPending}
                          >
                            {save.isPending && save.variables?.id === stage.id
                              ? 'A gravar…'
                              : 'Salvar prompt'}
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Restaurar o prompt padrão de fábrica deste estágio? Remove a sua versão salva e a produção volta ao prompt original.',
                                )
                              )
                                restore.mutate(stage);
                            }}
                            disabled={
                              restore.isPending ||
                              (!stage.is_overridden && value === stage.default_system)
                            }
                            title="Volta ao prompt de fábrica e remove o override salvo (backend + produção)"
                          >
                            {restore.isPending && restore.variables?.id === stage.id
                              ? 'A restaurar…'
                              : 'Restaurar padrão'}
                          </Button>
                          {stage.is_overridden && (
                            <span className={styles.overrideHint}>● override salvo</span>
                          )}
                          <span className={styles.saidaHint}>{stage.saida}</span>
                        </div>
                      </div>

                      {/* COLUNA PRETA — saída do estágio */}
                      <div className={styles.outputCol}>
                        <span className={styles.outLabel}>Saída</span>
                        {!out && (
                          <p className={styles.outEmpty}>
                            Rode o fluxo (ou até aqui) para ver a saída desta etapa.
                          </p>
                        )}
                        {out && out.ok && <pre className={styles.outPre}>{out.output}</pre>}
                        {out && !out.ok && (
                          <pre className={styles.outErr}>⚠ {out.error}</pre>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
